import { Link, useLocation, useNavigate } from "react-router-dom";
import { CartDrawer } from "./CartDrawer";
import { WholesaleBanner } from "./WholesaleBanner";
import { CurrencySwitcher } from "./CurrencySwitcher";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { User, LogOut, Shield, Menu } from "lucide-react";
import { useState } from "react";
import logo from "@/assets/logo-clean-transparent.webp";

export const StoreHeader = () => {
  const { user, isAdmin, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const goToHash = (hash: string) => {
    if (location.pathname === "/") {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      const scrollToElement = (attempts = 0) => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        } else if (attempts < 10) {
          setTimeout(() => scrollToElement(attempts + 1), 100);
        }
      };
      setTimeout(() => scrollToElement(), 150);
    }
  };

  const handleHashLink = (hash: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    goToHash(hash);
  };

  const handleMobileHash = (hash: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => goToHash(hash), 50);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <WholesaleBanner />
      <div className="container flex h-20 items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="Param Kesar®" className="h-16 w-auto" width={200} height={64} fetchPriority="high" decoding="async" />
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <a href="#products" onClick={handleHashLink("products")} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Products
          </a>
          <a href="#about" onClick={handleHashLink("about")} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Our Story
          </a>
          <Link to="/thekesarkitchen" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            The Kesar Kitchen™
          </Link>
          <Link to="/contact" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Contact
          </Link>
        </nav>
        <div className="flex items-center gap-1 md:gap-2">
          {user ? (
            <>
              {isAdmin && (
                <Link to="/admin" title="Admin" className="hidden sm:inline-flex">
                  <Button variant="ghost" size="icon">
                    <Shield className="h-4 w-4" />
                  </Button>
                </Link>
              )}
              <Link to="/account" title="My account" className="hidden sm:inline-flex">
                <Button variant="ghost" size="icon">
                  <User className="h-4 w-4" />
                </Button>
              </Link>
              <Button type="button" variant="ghost" size="icon" onClick={async () => { await signOut(); navigate('/'); }} title="Sign out" className="hidden sm:inline-flex">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Link to="/auth" title="Sign in" className="hidden sm:inline-flex">
              <Button variant="ghost" size="icon">
                <User className="h-4 w-4" />
              </Button>
            </Link>
          )}
          <CartDrawer />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[80vw] sm:w-80">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <nav className="mt-8 flex flex-col gap-1">
                <a href="#products" onClick={handleMobileHash("products")} className="px-2 py-3 text-lg font-serif border-b border-border">
                  Products
                </a>
                <a href="#about" onClick={handleMobileHash("about")} className="px-2 py-3 text-lg font-serif border-b border-border">
                  Our Story
                </a>
                <SheetClose asChild>
                  <Link to="/thekesarkitchen" className="px-2 py-3 text-lg font-serif border-b border-border">
                    The Kesar Kitchen™
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to="/contact" className="px-2 py-3 text-lg font-serif border-b border-border">
                    Contact
                  </Link>
                </SheetClose>

                <div className="mt-6 flex flex-col gap-2">
                  {user ? (
                    <>
                      {isAdmin && (
                        <SheetClose asChild>
                          <Link to="/admin">
                            <Button variant="outline" className="w-full justify-start gap-2">
                              <Shield className="h-4 w-4" /> Admin
                            </Button>
                          </Link>
                        </SheetClose>
                      )}
                      <SheetClose asChild>
                        <Link to="/account">
                          <Button variant="outline" className="w-full justify-start gap-2">
                            <User className="h-4 w-4" /> My account
                          </Button>
                        </Link>
                      </SheetClose>
                      <Button
                        variant="outline"
                        className="w-full justify-start gap-2"
                        onClick={async () => { setOpen(false); await signOut(); navigate('/'); }}
                      >
                        <LogOut className="h-4 w-4" /> Sign out
                      </Button>
                    </>
                  ) : (
                    <SheetClose asChild>
                      <Link to="/auth">
                        <Button variant="outline" className="w-full justify-start gap-2">
                          <User className="h-4 w-4" /> Sign in
                        </Button>
                      </Link>
                    </SheetClose>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
